import { DEFAULT_MEDIA_DURATION, PROJECT_MEDIA_TYPES } from 'consts'
import { getStringifiedQuality } from 'store/features/aiVideo/utils'
import { convertAspectRatioToNumber } from 'utils/aspectRatio'
import {
  IAIImageModel,
  IAIImageProject,
  IGenerateFormState,
  IGenerateImagePayload,
  IReferenceImage,
  TAspectRatio
} from 'store/types/aiImage'
import { MediaStatus } from 'types/media'
import { convertBaseItemToMediaItem } from 'utils/media'
import { isDeepEqual } from 'utils'

export const getImageModelQuality = (
  img: IAIImageProject
): string | undefined => {
  if (img.height) {
    return getStringifiedQuality(img.height)
  }
  return img.aspectRatio || undefined
}

export const normalizeImageVersionsResponse = (
  data: unknown
): IAIImageProject[] => {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (Array.isArray(obj.imageProjects)) return obj.imageProjects
    if (Array.isArray(obj.versions)) return obj.versions
  }
  return []
}

/**
 * Helper function to prepare the AI image generation payload
 * @param generateForm - The generate form is the form data from the UI
 * @returns The prepared form is the payload for the API call
 */
export const prepareGenerationPayload = (
  generateForm: IGenerateFormState,
  projectId: number
): IGenerateImagePayload => {
  const { modelCode, refImages, prompt, aspectRatio, styleId } = generateForm

  return {
    model: modelCode || '',
    refImages: refImages?.map((image: IReferenceImage) => image.url),
    prompt,
    aspectRatio: aspectRatio!,
    styleId: styleId || undefined,
    meta: {
      sourceProjectId: projectId,
      ...(generateForm.id ? { parentProjectId: generateForm.id } : {})
    },
    videoProjectId: projectId
  }
}

/**
 * Helper function to create a new pending media item for timeline
 * @param assetId - The asset ID is the imageId from the backend response
 * @param insertionStartTime - The start time where the item should be inserted
 * @param defaultDimensions - The default dimensions of the AI image
 * @returns The new media item
 */

export const createNewMediaItem = (
  imageProjectId: number,
  assetId: number,
  insertionStartTime: number,
  defaultDimensions: { aspectRatio: number; width: number; height: number },
  aspectRatio: string,
  zIndex: number = 0
) => {
  const aspectRatioNumber = convertAspectRatioToNumber(aspectRatio)
  const FAKE_HEIGHT = 100
  const FAKE_WIDTH = FAKE_HEIGHT * aspectRatioNumber

  const mediaItem = {
    path: null,
    type: PROJECT_MEDIA_TYPES.aiImage,
    originalWidth: FAKE_WIDTH,
    originalHeight: FAKE_HEIGHT,
    duration: DEFAULT_MEDIA_DURATION,
    fileName: `ai-image-${assetId}`,
    assetId: assetId.toString(),
    id: crypto.randomUUID(),
    status: MediaStatus.PENDING,
    assetParentId: imageProjectId.toString()
  }

  return convertBaseItemToMediaItem(
    mediaItem,
    insertionStartTime,
    defaultDimensions,
    zIndex
  )
}

/**
 * Helper function to check if the form has changes
 * @param current - The current form
 * @param original - The original form
 * @returns True if the form has changes, false otherwise
 */
export const hasFormChanges = <T extends object>(
  current: T,
  original: T
): boolean => {
  return Object.keys(original).some(key => {
    const currentValue = current[key as keyof T]
    const originalValue = original[key as keyof T]

    if (key === 'prompt' || key === 'refImages') {
      //Deep compare for objects and arrays
      if (typeof currentValue === 'object' && currentValue !== null) {
        return !isDeepEqual(currentValue, originalValue)
      }

      //Direct compare for primitives
      return currentValue !== originalValue
    }
  })
}

export const getAspectRatioValue = (aspectRatio: TAspectRatio) => {
  const [widthString, heightString] = aspectRatio.split(':')
  const width = Number(widthString)
  const height = Number(heightString)
  if (isNaN(width) || isNaN(height)) {
    return 0
  }
  return width / height
}

export const getClosestAspectRatio = (
  targetAspectRatio: TAspectRatio,
  modelAspectRatios: TAspectRatio[]
): TAspectRatio => {
  const targetAspectRatioValue = getAspectRatioValue(targetAspectRatio)
  const modelAspectRatiosValues = modelAspectRatios.map(getAspectRatioValue)
  const closestAspectRatio = modelAspectRatiosValues.reduce(
    (closest, current) => {
      return Math.abs(current - targetAspectRatioValue) <
        Math.abs(closest - targetAspectRatioValue)
        ? current
        : closest
    },
    modelAspectRatiosValues[0]
  )
  return modelAspectRatios[modelAspectRatiosValues.indexOf(closestAspectRatio)]
}

export const getCreditCostPerModel = ({
  modelCode,
  modelsData
}: {
  modelCode: string
  modelsData: IAIImageModel[]
}) => {
  const model = modelsData.find(model => model.code === modelCode)
  return model?.cost || 0
}
